import React, { useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import {
  Bar, CartesianGrid, ComposedChart, LabelList, Line,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import {
  getInterruptionsSummary, getInterruptionsTrend,
  getInterruptionsDetail, getExperienceSummary,
} from "../../services/ops/opsservices";
import { useClientTimeZone, zoneAbbr } from "../../utils/clienttimezone";
import ConversationDialog from "./ConversationDialog";
import {
  BLUE, GREEN, AMBER, RED, GRAY, NAVY,
  CounterCard, ChartCard, SectionTitle, Caption, DetailTable, LoadingRow,
  OpsSlicers, StatusChip, identityColumns, drillColumn, useOpsData,
  DEFAULT_GRAN, DEFAULT_RANGE, getDateRange,
  formatRangeLabel, isSubDayGran,
  fmtInt, fmtPct, fmtDate, fmtAxisDate, fmtTime, safeNum,
} from "./opsShared";

export default function ServiceInterruptionsDashboard() {
  const [gran,  setGran]  = useState(DEFAULT_GRAN);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const { timeZone, tzReady } = useClientTimeZone();
  const tzLabel = zoneAbbr(timeZone);

  const params = useMemo(() => ({
    ...getDateRange(range, timeZone),
    granularity: gran.toLowerCase(),
    // No tz param: the backend resolves the tenant zone from clientconfig, so
    // every viewer sees the same day boundaries regardless of their browser.
  }), [range, gran, timeZone]);

  // Any account row opens its full conversation.
  const [drill, setDrill] = useState(null);
  const openThread = (row) => setDrill(row);

  const { data, loading, error } = useOpsData({
    summary:    getInterruptionsSummary,
    trend:      getInterruptionsTrend,
    detail:     getInterruptionsDetail,
    experience: getExperienceSummary,
  }, params, tzReady);

  const s = data.summary || {};
  const x = data.experience || {};

  const interruptions = safeNum(s.serviceInterruptions);
  const repeats       = safeNum(s.repeatDeliveryAttempts);
  const unanswered    = safeNum(s.unansweredMessages);

  // These three have no benign level. One interrupted customer is one customer
  // told to phone in; one repeat delivery is one message that reached AI
  // twice. So the thresholds are 0, not a percentage band.
  const interruptStatus = interruptions === 0 ? "ok" : interruptions < 5 ? "warn" : "bad";
  const repeatStatus    = repeats === 0 ? "ok" : "bad";

  const trend = (data.trend || []).map((r) => ({
    date:          fmtAxisDate(r.summary_date, gran, timeZone),
    total:         safeNum(r.totalMessages),
    interruptions: safeNum(r.serviceInterruptions),
    repeats:       safeNum(r.repeatDeliveryAttempts),
    rate:          safeNum(r.interruptionRate),
  }));

  const rangeLabel = formatRangeLabel(params, timeZone);
  const titleOf = (t) => `${t} · ${rangeLabel}`;

  const detailColumns = [
    drillColumn(openThread),
    ...identityColumns,
    { key: "customerMessage", label: "Customer's Message", width: 220 },
    { key: "paymentVerificationNeeded", label: "Verify Payment", width: 120, align: "center",
      render: (r) => (
        safeNum(r.paymentVerificationNeeded) === 1
          ? <StatusChip label="Check PNM" tone="bad" />
          : <StatusChip label="No" tone="default" />
      ) },
    { key: "deliveryAttempts", label: "Attempts", width: 80, align: "right",
      render: (r) => safeNum(r.deliveryAttempts) },
    { key: "conversationEnded", label: "Chat Ended", width: 100, align: "center",
      render: (r) => (
        safeNum(r.conversationEnded) === 1
          ? <StatusChip label="Yes" tone="ok" />
          : <StatusChip label="Still open" tone="warn" />
      ) },
    { key: "receivedUtc", label: "Received", width: 160,
      render: (r) => fmtTime(r.receivedUtc, timeZone) },
    { key: "interruptedUtc", label: "Interrupted", width: 160,
      render: (r) => fmtTime(r.interruptedUtc, timeZone) },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <OpsSlicers gran={gran} setGran={setGran} range={range} setRange={setRange} tzLabel={tzLabel} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LoadingRow text="Checking for interruptions…" />}

      {!loading && (
        <>
          <SectionTitle>Service Interruptions</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <CounterCard label="Service Interruptions" value={fmtInt(interruptions)}
              color={GREEN} status={interruptStatus}
              sub="Customers told to call in" />
            <CounterCard label="Interruption Rate" value={fmtPct(s.interruptionRate)}
              color={GREEN} status={interruptStatus} />
            <CounterCard label="Repeat Deliveries" value={fmtInt(repeats)}
              color={GREEN} status={repeatStatus}
              sub="Messages that reached AI twice" />
            <CounterCard label="Unanswered Messages" value={fmtInt(unanswered)}
              color={GRAY} sub="Received, still no reply" />
            <CounterCard label="Repeat Messages" value={fmtInt(x.repeatMessages)}
              color={AMBER} sub="Customer texted again within 2 min" />
          </Box>

          {interruptions > 0 && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>
              {interruptions} {interruptions === 1 ? "customer was" : "customers were"} sent
              the fall-back message and asked to call 1-855-777-0279. Rows flagged
              "Check PNM" had a message that reads like a payment authorisation —
              confirm whether a charge went through before following up.
            </Alert>
          )}

          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("Interruptions over time")} height={280}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, "auto"]}
                    tick={{ fontSize: 10, fill: RED }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  {/* Total volume sits behind in a muted fill so a spike in the
                      rate can be read against how many messages it came from —
                      3 interruptions out of 5 is not 3 out of 900. */}
                  <Bar yAxisId="left" dataKey="total" name="Messages handled"
                    fill={`${NAVY}22`} radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="left" dataKey="interruptions" name="Interruptions"
                    fill={RED} radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="interruptions" position="top" fontSize={10} fill={RED}
                      formatter={(v) => (v > 0 ? v : "")} />
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="rate" name="Interruption rate"
                    stroke={RED} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("Messages delivered to AI more than once")} height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="repeats" name="Repeat deliveries" fill={AMBER} radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="repeats" position="top" fontSize={10} fill={AMBER}
                      formatter={(v) => (v > 0 ? v : "")} />
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
            <Caption>
              This must read zero from 15 August onward. A message reaching AI twice is the
              condition that made duplicate payments possible; the single-delivery guarantee
              in the worker is what this chart verifies.
            </Caption>
          </Box>

          <SectionTitle right={`${(data.detail || []).length} shown`}>
            Interruption Detail
          </SectionTitle>
          <DetailTable
            columns={detailColumns}
            rows={data.detail || []}
            emptyText="No service interruptions in this range."
            maxHeight={440}
          />
          <Caption>
            "Chat Ended" should read Yes on every row — the fall-back closes the conversation
            so no further message reaches AI. A row showing "Still open" means the closing
            step did not complete and the customer could reach the agent again.
          </Caption>
        </>
      )}

      <ConversationDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        customerdataid={drill?.customerdataid}
      />
    </Box>
  );
}